import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function JiraImport() {
  const { projects } = useApp();
  const [projectId, setProjectId] = useState("");
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const submit = async () => {
    if (!projectId) { toast.error("Choose a project"); return; }
    if (!file) { toast.error("Select a CSV file"); return; }
    setLoading(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/import/csv?project_id=${projectId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`Imported ${r.data.created} issues`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="px-6 py-6 max-w-[900px]">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Migrate</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Import from Jira (CSV)</h1>
        <p className="text-sm text-muted-foreground mt-1">Export issues from Jira as CSV, then map them into a FlowTrack project.</p>
      </div>
      <Card className="p-6 space-y-4">
        <div>
          <Label>Target project</Label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger data-testid="import-project"><SelectValue placeholder="Choose a project" /></SelectTrigger>
            <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.key})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>CSV file</Label>
          <div className="border border-dashed border-border rounded-md p-6 flex flex-col items-center gap-2 text-sm cursor-pointer hover:border-primary/40 transition-colors" onClick={()=>inputRef.current?.click()} data-testid="import-dropzone">
            <FileSpreadsheet className="w-6 h-6 text-primary" />
            {file ? <span className="font-mono">{file.name} <span className="text-muted-foreground">({(file.size/1024).toFixed(1)} KB)</span></span>
              : <span className="text-muted-foreground">Click to select a CSV file</span>}
            <input ref={inputRef} type="file" accept=".csv,text/csv" hidden onChange={(e)=>setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
          </div>
        </div>
        <div className="text-xs text-muted-foreground border border-border rounded-md p-3 bg-secondary/40">
          <div className="font-medium text-foreground mb-1">Recognized columns (case-insensitive)</div>
          <div className="grid grid-cols-2 gap-1 font-mono">
            <span>Summary / Title</span>
            <span>Description</span>
            <span>Issue Type (Epic/Story/Task/Bug…)</span>
            <span>Status (To Do/In Progress/Done…)</span>
            <span>Priority (Highest/High/Medium/Low/Lowest)</span>
            <span>Assignee (name or email)</span>
            <span>Reporter</span>
            <span>Labels (comma-separated)</span>
            <span>Story Points</span>
          </div>
        </div>
        <Button onClick={submit} disabled={loading} data-testid="import-submit" className="gap-1.5"><Upload className="w-4 h-4" /> {loading ? "Importing…" : "Import"}</Button>
      </Card>
      {result && (
        <Card className="p-5 mt-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            <div className="font-display text-lg font-semibold">Import complete</div>
          </div>
          <div className="text-sm">Created <span className="font-mono font-semibold">{result.created}</span> issues from <span className="font-mono">{result.total_rows}</span> rows.</div>
          {result.errors?.length > 0 && (
            <div className="mt-3 text-xs">
              <div className="flex items-center gap-1 text-amber-500 font-medium mb-1"><AlertCircle className="w-3.5 h-3.5" /> {result.errors.length} row error{result.errors.length>1?"s":""}</div>
              <ul className="list-disc list-inside space-y-0.5 text-muted-foreground font-mono">
                {result.errors.map((e,i)=><li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
